/**
 * @file canvas-cart-messages.ts
 * @description Мост кабинета и канвы для корзины (phase19.16, партия SB120,
 * этап 4; db-design-sb120-stage4 §5, uiux-cart §7). Контракт с витриной
 * (apps/web components/site/builder/preview-bridge.ts):
 *
 * Кабинет → канва (маркер `source: "commora-admin"`):
 *  - `commora:canvas-cart { open, view }` — открыть или закрыть корзину на
 *    канве; `view: "peek"` — показать «Окно на 4 с» (выбор в «После
 *    «В корзину»»), `"cart"` — открытая корзина с образцом.
 * Канва → кабинет (маркер `source: "chaiberi-preview"`):
 *  - `canvas-cart-state { open, sample }` — после монтирования и после
 *    каждого `commora:canvas-cart`; нет ответа за `CANVAS_CART_REPLY_MS` —
 *    кабинет показывает «Не удалось показать корзину…» с «Повторить»;
 *  - `area-select { area: "cart" }` — нажатие по корзине на канве (SB121):
 *    кабинет открывает «Вид корзины».
 * Вид и цвета корзины канва получает живой темой (`commora:site-theme-preview`
 * с разделом `cart`) — нового сообщения для них нет.
 */

import type { SiteCartAfterAdd } from "@chaiberi/shared";

import { CABINET_MESSAGE_SOURCE, CANVAS_MESSAGE_SOURCE } from "./canvas-messages";

/** Тип сообщения кабинета: открыть/закрыть корзину на канве. */
export const CANVAS_CART_MESSAGE = "commora:canvas-cart";
/** Тип ответа канвы. */
export const CANVAS_CART_STATE = "canvas-cart-state";
/** Сколько ждать ответа канвы (db-design-sb120-stage4 §5: ~3 с). */
export const CANVAS_CART_REPLY_MS = 3000;

/** Текст состояния «канва не открыла корзину» (uiux-cart §7). */
export const CANVAS_CART_FAILED_TEXT = "Не удалось показать корзину на образце сайта. Настройки сохраняются — проверьте вид после публикации.";
/** Корзина на образце закрыта (×, Esc, конец «окна на 4 с») — ревью 2026-10-19. */
export const CANVAS_CART_CLOSED_TEXT = "Корзина на образце закрыта.";

/** Подпись образца на канве (uiux-cart §7). */
export const CANVAS_CART_SAMPLE_TEXT = "Образец: 3 товара. Корзина покупателя здесь не видна.";

/** Что показать на канве: открытую корзину или «Окно на 4 с». */
export type CanvasCartView = "cart" | "peek";

/** Вид показа по выбору «После «В корзину»»: «Окно на 4 с» — показать его, иначе — открытая корзина. */
export function canvasCartViewFor(afterAdd: SiteCartAfterAdd): CanvasCartView {
  return afterAdd === "peek" ? "peek" : "cart";
}

export function canvasCartMessage(open: boolean, view: CanvasCartView) {
  return { source: CABINET_MESSAGE_SOURCE, type: CANVAS_CART_MESSAGE, open, view } as const;
}

/** Ответ канвы о корзине. */
export interface CanvasCartState {
  open: boolean;
  /** На канве образец (а не корзина владельца). */
  sample: boolean;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** `canvas-cart-state` от канвы; `null` — не то сообщение. */
export function canvasCartStateOf(data: unknown): CanvasCartState | null {
  if (!isObject(data) || data.source !== CANVAS_MESSAGE_SOURCE || data.type !== CANVAS_CART_STATE) return null;
  return { open: data.open === true, sample: data.sample === true };
}

/** `area-select { area: "cart" }` — нажали по корзине на канве. */
export function cartPickedOnCanvas(data: unknown): boolean {
  return isObject(data) && data.source === CANVAS_MESSAGE_SOURCE && data.type === "area-select" && data.area === "cart";
}
